import {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useRef,
  useState
} from 'react'

import cn from 'classnames'

import { PlayIcon } from '@innoscripta/icons'

import {
  DEFAULT_CONTROLS_HIDE_DELAY,
  DEFAULT_VIDEO_PLAYER_FEATURES
} from './VideoPlayer.constants'
import styles from './VideoPlayer.module.scss'
import { VideoPlayerProps } from './VideoPlayer.types'
import { VideoPlayerControls } from './VideoPlayerControls'
import { useControlsVisibility } from './useControlsVisibility'
import { useVideoFullscreen } from './useVideoFullscreen'
import { useVideoPlayerController } from './useVideoPlayerController'
import { useVideoPlayerLabels } from './useVideoPlayerLabels'

const VideoPlayerComponent: ForwardRefRenderFunction<
  HTMLVideoElement,
  VideoPlayerProps
> = (
  {
    features: featureOverrides,
    labels: labelOverrides,
    className,
    videoClassName,
    autoHideControls = true,
    controlsHideDelay = DEFAULT_CONTROLS_HIDE_DELAY,
    src,
    onClick,
    onDurationChange,
    onEnded,
    onLoadedMetadata,
    onPause,
    onPlay,
    onRateChange,
    onTimeUpdate,
    onVolumeChange,
    ...videoProps
  },
  forwardedRef
) => {
  const playerRef = useRef<HTMLDivElement>(null)
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false)
  const features = { ...DEFAULT_VIDEO_PLAYER_FEATURES, ...featureOverrides }
  const labels = useVideoPlayerLabels(labelOverrides)
  const hasControls = Object.values(features).some(Boolean)
  const { state, actions, eventHandlers, setVideoRef, videoRef } =
    useVideoPlayerController({
      forwardedRef,
      src,
      onDurationChange,
      onEnded,
      onLoadedMetadata,
      onPause,
      onPlay,
      onRateChange,
      onTimeUpdate,
      onVolumeChange
    })
  const { isFullscreen, toggleFullscreen } = useVideoFullscreen({
    playerRef,
    videoRef
  })
  const { areControlsVisible, keepControlsVisible, revealControls } =
    useControlsVisibility({
      autoHide: autoHideControls,
      delay: controlsHideDelay,
      enabled: hasControls,
      isLocked: isSpeedMenuOpen,
      isPlaying: state.isPlaying
    })

  useEffect(() => setIsSpeedMenuOpen(false), [src])

  return (
    <div
      ref={playerRef}
      className={cn(styles.player, className, {
        [styles.playerCursorHidden]: state.isPlaying && !areControlsVisible
      })}
      data-cursor-hidden={state.isPlaying && !areControlsVisible}
      data-video-player
      onMouseMove={revealControls}
      onFocusCapture={keepControlsVisible}
      onBlurCapture={event => {
        if (
          event.relatedTarget instanceof Node &&
          event.currentTarget.contains(event.relatedTarget)
        ) {
          return
        }
        revealControls()
      }}
    >
      <video
        {...videoProps}
        {...eventHandlers}
        ref={setVideoRef}
        src={src}
        controls={false}
        className={cn(styles.video, videoClassName)}
        aria-label={videoProps['aria-label'] ?? labels.video}
        onClick={event => {
          onClick?.(event)
          if (!event.defaultPrevented && features.playPause) {
            actions.togglePlayback()
          }
        }}
      />

      {features.playPause && (
        <div
          className={cn(styles.pauseOverlay, {
            [styles.pauseOverlayVisible]: !state.isPlaying
          })}
          data-paused={!state.isPlaying}
          aria-hidden='true'
        />
      )}

      {features.playPause && (
        <button
          type='button'
          className={cn(styles.centerPlayButton, {
            [styles.centerPlayButtonVisible]: !state.isPlaying
          })}
          onClick={actions.togglePlayback}
          aria-label={labels.play}
          aria-hidden={state.isPlaying}
          tabIndex={state.isPlaying ? -1 : 0}
        >
          <PlayIcon size={28} fill='currentColor' aria-hidden='true' />
        </button>
      )}

      {hasControls && (
        <VideoPlayerControls
          areVisible={areControlsVisible}
          currentTime={state.currentTime}
          duration={state.duration}
          features={features}
          isFullscreen={isFullscreen}
          isMuted={state.isMuted}
          isPlaying={state.isPlaying}
          isSpeedMenuOpen={isSpeedMenuOpen}
          labels={labels}
          playbackRate={state.playbackRate}
          setIsSpeedMenuOpen={setIsSpeedMenuOpen}
          volume={state.volume}
          onKeepVisible={keepControlsVisible}
          onPlaybackRateChange={actions.setRate}
          onReveal={revealControls}
          onSeek={actions.seek}
          onToggleFullscreen={toggleFullscreen}
          onToggleMute={actions.toggleMute}
          onTogglePlayback={actions.togglePlayback}
          onVolumeInput={actions.setVolumeFromSlider}
        />
      )}
    </div>
  )
}

export const VideoPlayer = forwardRef(VideoPlayerComponent)
export { formatVideoTime } from './VideoPlayer.utils'
